import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import Fingerprint2 from 'fingerprintjs2';
import { URLS } from '../constants/URLS';

@Injectable({ providedIn: 'root' })
export class GuestTokenService {
  private _isGuest = false;

  constructor(private http: HttpClient) {
    this._isGuest = !!localStorage.getItem('guestToken');
  }

  get isGuest(): boolean {
    // A user holding a refreshToken (the 30-day idToken) is LOGGED IN, even if
    // the short-lived access `token` was momentarily removed mid-refresh. Without
    // the refreshToken check, a concurrent 401 during the refresh window sees
    // `!token` and wrongly treats the user as a guest → logs them out. Guest is
    // only ever true when there's no token AND no refreshToken.
    return this._isGuest && !localStorage.getItem('token') && !localStorage.getItem('refreshToken');
  }

  getGuestToken(): string | null {
    return localStorage.getItem('guestToken');
  }

  requestGuestToken(): Observable<string> {
    return from(this.getOrCreateDeviceId()).pipe(
      switchMap(deviceId => {
        const body = { deviceId, companyId: 1 };
        return this.http.post<any>(URLS.guestToken, body).pipe(
          map(response => {
            if (response.status === 'SUCCESS' && response.idToken) {
              localStorage.setItem('guestToken', response.idToken);
              this._isGuest = true;
              return response.idToken;
            }
            throw new Error(response.message || 'Guest token failed');
          }),
          catchError(error => {
            if (error.status === 409) {
              this._isGuest = false;
              localStorage.removeItem('guestToken');
            }
            return throwError(error);
          })
        );
      })
    );
  }

  clearGuestState(): void {
    localStorage.removeItem('guestToken');
    this._isGuest = false;
  }

  /**
   * Stable, deterministic per-device id. Reuses an existing real id; otherwise
   * computes a browser fingerprint (same algorithm login uses) so guest → login
   * → OTP all share ONE id per physical device. Any legacy throwaway id is
   * migrated to the fingerprint — that churn was creating a new user_device row
   * (and a deactivation) on every fresh session.
   */
  /** A real device id is the 32-char hex from Fingerprint2.x64hash128. Anything
      else (missing, "null", or a legacy throwaway id) is migrated to a real one. */
  private static readonly DEVICE_ID_RE = /^[a-f0-9]{32}$/i;

  private async getOrCreateDeviceId(): Promise<string> {
    const existing = localStorage.getItem('deviceId');
    if (existing && GuestTokenService.DEVICE_ID_RE.test(existing)) {
      return existing;
    }
    const fingerprint = await this.computeFingerprint();
    localStorage.setItem('deviceId', fingerprint);
    return fingerprint;
  }

  private computeFingerprint(): Promise<string> {
    return new Promise<string>(resolve => {
      try {
        Fingerprint2.get((components: any[]) => {
          const values = components.map(c => c.value);
          resolve(Fingerprint2.x64hash128(values.join(''), 31));
        });
      } catch {
        // Fingerprint unavailable → random 32-hex id (valid format, so it's
        // stable and won't be regenerated on the next call).
        let hex = '';
        for (let i = 0; i < 32; i++) { hex += Math.floor(Math.random() * 16).toString(16); }
        resolve(hex);
      }
    });
  }
}
